import React, { useState, useEffect } from "react"
import { User, Mail, Lock, LogOut, AlertCircle } from "lucide-react"
import { useFirebase } from "../context/FirebaseContext"

const ProfilePage = ({ currentLanguage, currentFlashcards, streak, handleTabClick }) => {
	const { 
		currentUser, 
		authLoading, 
		authError, 
		signUp, 
		signIn, 
		logOut,
		getUserData
	} = useFirebase()

	const [isLogin, setIsLogin] = useState(true)
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [displayName, setDisplayName] = useState("")
	const [error, setError] = useState("")
	const [userData, setUserData] = useState(null)
	const [userLoading, setUserLoading] = useState(false)

	// Fetch user data when user logs in
	useEffect(() => {
		const fetchUserData = async () => {
			if (currentUser) {
				setUserLoading(true)
				try {
					const data = await getUserData(currentUser.uid)
					setUserData(data)
				} catch (error) {
					console.error("Error fetching user data:", error)
				} finally {
					setUserLoading(false)
				}
			} else {
				setUserData(null)
			}
		}

		fetchUserData()
	}, [currentUser, getUserData])

	const handleSubmit = async (e) => {
		e.preventDefault()
		setError("")

		try {
			if (isLogin) {
				await signIn(email, password)
				// Redirect to home page after successful login
				if (handleTabClick) {
					handleTabClick("home")
				}
			} else {
				await signUp(email, password, displayName)
				// Redirect to home page after successful signup
				if (handleTabClick) {
					handleTabClick("home")
				}
			}
		} catch (error) {
			setError(error.message)
		}
	}

	const handleLogout = async () => {
		try {
			await logOut()
		} catch (error) {
			setError(error.message)
		}
	}

	// Loading state
	if (authLoading || userLoading) {
		return (
			<div className="flex justify-center items-center py-10">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3A6E]"></div>
			</div>
		)
	}

	// If user is logged in, show profile
	if (currentUser) {
		const displayNameValue = userData?.displayName || currentUser.displayName || "User"
		const userStreak = userData?.streak || streak
		
		// Get learning goal from user data or localStorage
		let learningGoal = "10 min/day"
		if (userData?.learningGoal) {
			learningGoal = userData.learningGoal
		} else {
			// Fallback to localStorage for non-Firebase stored goals
			const savedSettings = localStorage.getItem("hopLanguageAppSettings")
			if (savedSettings) {
				const parsed = JSON.parse(savedSettings)
				if (parsed.dailyGoal) {
					learningGoal = `${parsed.dailyGoal} min/day`
				}
			}
		}

		return (
			<div className="text-center py-10">
				<div className="bg-[#1E1E1E] rounded-full h-24 w-24 flex items-center justify-center mx-auto mb-4">
					<User size={48} className="text-[#6E6E6E]" />
				</div>
				<h2 className="text-xl font-bold mb-2">{displayNameValue}'s Profile</h2>
				<p className="text-[#A0A0A0] mb-6">
					Manage your profile and learning preferences
				</p>

				<div className="bg-[#1E1E1E] rounded-lg p-4 mb-4">
					<div className="flex justify-between items-center">
						<span className="text-white">Email</span>
						<span className="text-white">{currentUser.email}</span>
					</div>
				</div>

				<div className="bg-[#1E1E1E] rounded-lg p-4 mb-4">
					<div className="flex justify-between items-center">
						<span className="text-white">Current Streak</span>
						<span className="text-orange-500 font-bold">{userStreak} days</span>
					</div>
				</div>

				<div className="bg-[#1E1E1E] rounded-lg p-4 mb-4">
					<div className="flex justify-between items-center">
						<span className="text-white">Learning Goal</span>
						<span className="text-green-500">{learningGoal}</span>
					</div>
				</div>

				<div className="bg-[#1E1E1E] rounded-lg p-4 mb-4">
					<div className="flex justify-between items-center">
						<span className="text-white">Currently Learning</span>
						<div className="flex items-center">
							<span className="text-xl mr-2">{currentLanguage.flag}</span>
							<span className="text-white">{currentLanguage.name}</span>
						</div>
					</div>
				</div>

			

				<button
					className="bg-[#2C2C2C] text-white py-2 px-6 rounded-lg flex items-center mx-auto hover:bg-[#3C3C3C]"
					onClick={handleLogout}
				>
					<LogOut size={18} className="mr-2" />
					Sign Out
				</button>
			</div>
		)
	}

	// If user is not logged in, show login/signup form
	return (
		<div className="max-w-md mx-auto py-10">
			<div className="bg-[#1E1E1E] rounded-full h-24 w-24 flex items-center justify-center mx-auto mb-4">
				<User size={48} className="text-[#6E6E6E]" />
			</div>
			
			<h2 className="text-xl font-bold text-center mb-2">
				{isLogin ? "Sign In to Your Account" : "Create an Account"}
			</h2>
			<p className="text-[#A0A0A0] text-center mb-6">
				{isLogin 
					? "Access your profile and track your learning progress" 
					: "Join HedgeHop to save your progress and track your learning"}
			</p>

			{(error || authError) && (
				<div className="bg-red-900/30 text-red-400 p-3 rounded-lg mb-4 flex items-start">
					<AlertCircle size={18} className="mr-2 flex-shrink-0 mt-0.5" />
					<span>{error || authError}</span>
				</div>
			)}

			<form onSubmit={handleSubmit} className="space-y-4">
				{!isLogin && (
					<div className="bg-[#1E1E1E] rounded-lg p-4">
						<label className="block text-[#A0A0A0] text-sm mb-1">Name</label>
						<div className="flex">
							<div className="bg-[#2C2C2C] p-2 rounded-l-lg">
								<User size={20} className="text-[#6E6E6E]" />
							</div>
							<input
								type="text"
								value={displayName}
								onChange={(e) => setDisplayName(e.target.value)}
								className="bg-[#2C2C2C] text-white p-2 rounded-r-lg w-full"
								placeholder="Your name"
								required={!isLogin}
							/>
						</div>
					</div>
				)}

				<div className="bg-[#1E1E1E] rounded-lg p-4">
					<label className="block text-[#A0A0A0] text-sm mb-1">Email</label>
					<div className="flex">
						<div className="bg-[#2C2C2C] p-2 rounded-l-lg">
							<Mail size={20} className="text-[#6E6E6E]" />
						</div>
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="bg-[#2C2C2C] text-white p-2 rounded-r-lg w-full"
							placeholder="email@example.com"
							required
						/>
					</div>
				</div>

				<div className="bg-[#1E1E1E] rounded-lg p-4">
					<label className="block text-[#A0A0A0] text-sm mb-1">Password</label>
					<div className="flex">
						<div className="bg-[#2C2C2C] p-2 rounded-l-lg">
							<Lock size={20} className="text-[#6E6E6E]" />
						</div>
						<input
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							className="bg-[#2C2C2C] text-white p-2 rounded-r-lg w-full"
							placeholder="••••••••"
							required
						/>
					</div>
				</div>

				<button
					type="submit"
					className="bg-gradient-to-r from-[#FF3A6E] to-[#FF6D4B] rounded-lg p-3 text-white font-semibold w-full"
				>
					{isLogin ? "Sign In" : "Create Account"}
				</button>
			</form>

			<div className="text-center mt-6">
				<button
					className="text-[#A0A0A0] hover:text-white"
					onClick={() => {
						setIsLogin(!isLogin)
						setError("")
					}}
				>
					{isLogin 
						? "Don't have an account? Sign up" 
						: "Already have an account? Sign in"}
				</button>
			</div>
		</div>
	)
}

export default ProfilePage